import { MotionReveal } from "../components/MotionReveal";
import { Brain, Boxes, Cpu, Layers, ArrowRight } from "lucide-react";

const pillars = [
  {
    title: "Governance Layer",
    desc: [
      "Standards & best practices",
      "Tool selection & rationalization",
      "Cross-functional alignment",
      "Vendor management",
    ],
    icon: Layers,
  },
  {
    title: "Architecture Authority",
    desc: [
      "Reference architectures",
      "Integration patterns",
      "Data flow standards",
      "Platform engineering",
    ],
    icon: Boxes,
  },
  {
    title: "AI Intelligence Engine",
    desc: [
      "Correlation & analytics",
      "Anomaly detection",
      "Predictive insights",
      "Root cause analysis",
    ],
    icon: Brain,
  },
  {
    title: "Automation Backbone",
    desc: [
      "Runbook automation",
      "Self-healing capabilities",
      "Orchestration workflows",
      "Reliability engineering",
    ],
    icon: Cpu,
  },
];

export default function CentreOfExcellencePage() {
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="pt-6 sm:pt-10">
        <MotionReveal>
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.18em] text-[var(--text-secondary)]">
              Governance. Architecture. Intelligence. Automation.
            </div>
            <h1 className="mt-6 font-[var(--font-display)] text-4xl sm:text-5xl tracking-tight text-white">
              The Observability Centre of Excellence
            </h1>
            <p className="mt-4 max-w-3xl text-sm sm:text-base text-[var(--text-secondary)]">
              A Centre of Excellence is not a tool. It’s an organizational capability — a strategic layer that brings governance,
              architecture standards, AI-powered intelligence, and automation together to transform how enterprises operate.
            </p>
          </div>
        </MotionReveal>
      </div>

      <section className="mt-10 sm:mt-14">
        <MotionReveal>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {pillars.map((p, idx) => (
              <MotionReveal key={p.title} delay={Math.min(0.3, idx * 0.05)}>
                <div className="glass-panel hover-lift rounded-3xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/5 text-[var(--accent-primary)]">
                      <p.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-[var(--font-display)] text-lg tracking-tight text-white">{p.title}</div>
                      <ul className="mt-3 grid gap-2 text-sm text-[var(--text-secondary)]">
                        {p.desc.map((d) => (
                          <li key={d} className="flex gap-3">
                            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--accent-primary)] opacity-70" />
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </MotionReveal>
            ))}
          </div>
        </MotionReveal>
      </section>

      <section className="mt-16 sm:mt-20">
        <MotionReveal>
          <div className="grid gap-8 lg:grid-cols-[0.5fr_0.5fr] lg:items-start">
            <div className="glass-panel rounded-3xl p-6 sm:p-8">
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">Why CoE vs tool sprawl</div>
              <div className="mt-3 font-[var(--font-display)] text-2xl tracking-tight text-white">Consolidate decisions, not just data</div>
              <div className="mt-4 grid gap-2 text-sm">
                {[
                  ["Multiple dashboards, no single truth", "Unified operational view"],
                  ["Siloed teams, duplicated effort", "Cross-functional excellence"],
                  ["Vendor lock-in", "Best-of-breed flexibility"],
                  ["High TCO, low ROI", "Optimized investment"],
                  ["Alert noise", "Actionable intelligence"],
                ].map(([a, b]) => (
                  <div key={a} className="grid grid-cols-2 gap-3 rounded-2xl border border-white/10 bg-white/3 p-4">
                    <div className="text-[var(--text-secondary)]">{a}</div>
                    <div className="text-white">{b}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-panel rounded-3xl p-6 sm:p-8">
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">Reference architecture preview</div>
              <div className="mt-3 font-[var(--font-display)] text-2xl tracking-tight text-white">End-to-end telemetry to automation</div>
              <div className="mt-4 rounded-2xl border border-white/10 bg-[#0b1220]/60 p-5">
                <div className="grid gap-3 text-sm text-[var(--text-secondary)]">
                  <div>Data Sources → Telemetry Collection → Data Platforms</div>
                  <div>Correlation & Analytics → AI/ML & AIOps → Automation & Remediation</div>
                  <div>Executive Dashboards & SRE Consoles</div>
                </div>
              </div>
              <a
                href="/architecture"
                className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[var(--accent-primary)] hover:brightness-110"
              >
                View Architecture
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </MotionReveal>
      </section>

      <div className="h-12" />
    </div>
  );
}
